'use es6';

import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { BATTLESHIP } from 'HubStyleTokens/colors';
import { device } from '../constants/StyledComponentConstants';
import { jsx as _jsx } from "react/jsx-runtime";
const ScrollTrack = styled.div.withConfig({
  displayName: "Scrollbar__ScrollTrack",
  componentId: "sc-1jycqfi-0"
})(["margin-top:8px;position:relative;@media screen and ", "{display:none !important;}"], device.mobile);
const ScrollThumb = styled.div.withConfig({
  displayName: "Scrollbar__ScrollThumb",
  componentId: "sc-1jycqfi-1"
})(["height:10px;width:80px;top:-10px;border-radius:5px;background:", ";position:absolute;cursor:grab;"], BATTLESHIP);
const Scrollbar = ({
  scrollableContentRef
}) => {
  const scrollTrackRef = useRef();
  const scrollThumbRef = useRef();
  const dragStartClientX = useRef();
  const initialScrollLeft = useRef();
  const originalCursor = useRef(document.body.style.cursor);
  const [isDragging, setIsDragging] = useState(false);
  const handleScroll = useCallback(() => {
    const left = scrollableContentRef.current.scrollLeft;
    const maxLeft = scrollableContentRef.current.scrollWidth - scrollableContentRef.current.offsetWidth;
    const thumbPosition = left / maxLeft * (scrollTrackRef.current.offsetWidth - scrollThumbRef.current.offsetWidth);
    scrollThumbRef.current.style.left = `${thumbPosition}px`;
    scrollTrackRef.current.style.display = maxLeft ? 'block' : 'none';
  }, [scrollableContentRef]);
  const handleMouseDown = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    dragStartClientX.current = e.clientX;
    initialScrollLeft.current = scrollableContentRef.current.scrollLeft;
    document.body.style.cursor = 'grabbing';
    setIsDragging(true);
  }, [scrollableContentRef]);
  const handleMouseUp = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    document.body.style.cursor = originalCursor.current;
    setIsDragging(false);
  }, []);
  const handleMouseMove = useCallback(e => {
    const maxLeftContent = scrollableContentRef.current.scrollWidth - scrollableContentRef.current.offsetWidth;
    const maxLeftTrack = scrollTrackRef.current.offsetWidth - scrollThumbRef.current.offsetWidth;
    const scaledDeltaX = (e.clientX - dragStartClientX.current) * (maxLeftContent / maxLeftTrack);
    scrollableContentRef.current.scrollLeft = Math.min(initialScrollLeft.current + scaledDeltaX, scrollableContentRef.current.scrollWidth - scrollableContentRef.current.offsetWidth);
  }, [scrollableContentRef]);
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
      };
    }
    return () => {};
  }, [isDragging, handleMouseMove]);
  useEffect(() => {
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseUp]);
  useEffect(() => {
    if (scrollableContentRef.current && scrollTrackRef.current) {
      const scrollable = scrollableContentRef.current;
      const scrollableChild = scrollableContentRef.current.firstChild;
      const observer = new ResizeObserver(handleScroll);
      scrollable.addEventListener('scroll', handleScroll);
      observer.observe(scrollable);
      observer.observe(scrollableChild);
      return () => {
        scrollable.removeEventListener('scroll', handleScroll);
        observer.unobserve(scrollable);
        observer.unobserve(scrollableChild);
      };
    }
    return () => {};
  }, [scrollableContentRef, handleScroll]);
  return /*#__PURE__*/_jsx(ScrollTrack, {
    ref: scrollTrackRef,
    children: /*#__PURE__*/_jsx(ScrollThumb, {
      onMouseDown: handleMouseDown,
      ref: scrollThumbRef
    })
  });
};
export default Scrollbar;